import { useState, type ReactNode } from 'react';
import { Inbox, Lightbulb, AlertCircle, ArrowRight } from 'lucide-react';
import type { SuggestionKind } from '@warriorlab/shared';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { SidePanel } from '@/components/ui/SidePanel';
import { ConfidenceBar } from '@/components/ui/ConfidenceBar';
import { InfoTip } from '@/components/ui/InfoTip';
import { Markdown } from '@/components/ui/Markdown';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';
import { timeAgo } from '@/lib/timeAgo';
import { api, ApiError, type Suggestion } from '@/lib/api';

/* ============================================================================
 * The one review queue (ported from swarm). Used in two scopes:
 *   • a project's Review tab — items pre-filtered to that project
 *   • the global /review page — items carry their project name inline
 * Each row carries its decision inline; open any item for the full reader.
 * Per-kind verdict verbs: next-actions and data-gaps are tasks/gaps for PEOPLE,
 * so their verdicts are task-shaped (done / resolved / dismiss); under the hood
 * all three pairs map onto the approve/decline endpoints.
 * ========================================================================== */

const KIND_META: Record<SuggestionKind, {
  label: string;
  icon: typeof Lightbulb;
  approve: string;
  decline: string;
  approvedMsg: string;
  declinedMsg: string;
  outcome: string;
}> = {
  LEARNING: {
    label: 'Learning', icon: Lightbulb, approve: 'Approve', decline: 'Decline',
    approvedMsg: 'Approved. Agents apply it from the next run.',
    declinedMsg: 'Declined.',
    outcome: 'Approve to store the knowledge below. Decline to reject it.',
  },
  DATA_GAP: {
    label: 'Data gap', icon: AlertCircle, approve: 'Resolved', decline: 'Dismiss',
    approvedMsg: 'Resolved.',
    declinedMsg: 'Dismissed.',
    outcome: 'The agent is missing this input. Mark it resolved once it’s been provided, or dismiss it if it isn’t relevant. Either way it clears from your queue.',
  },
  NEXT_ACTION: {
    label: 'Next action', icon: ArrowRight, approve: 'Mark done', decline: 'Dismiss',
    approvedMsg: 'Marked done.',
    declinedMsg: 'Dismissed.',
    outcome: 'This is a task for your team, not the agent. Mark it done when it’s handled, or dismiss it if it isn’t relevant.',
  },
};

/** One glossary line for every confidence display. */
export const CONFIDENCE_TIP =
  "The agent's confidence in this suggestion. Nothing is stored without your approval.";

/** Markdown → plain text for one-line previews (no asterisk soup in row cards). */
const stripMd = (s: string) =>
  s.replace(/\*\*|__|`/g, '').replace(/^#+\s*/gm, '').replace(/\n+/g, ' · ');

/** sources is JSON from the agent — coerce to a clean string list, never throw. */
export const sourceList = (sources: unknown): string[] =>
  Array.isArray(sources) ? sources.filter((s): s is string => typeof s === 'string') : [];

/* ------------------------------- the queue ------------------------------- */
export function ReviewQueue({
  pending,
  canDecide,
  projectNameOf,
  onDecided,
}: {
  pending: Suggestion[];
  canDecide: boolean;
  /** Global page: resolve a project name to show on the row. Project tab: omit. */
  projectNameOf?: (s: Suggestion) => string | null;
  onDecided: (msg?: string) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const open = pending.find((s) => s.id === openId) ?? null;

  const handled = (msg?: string) => {
    setOpenId(null);
    setBusyId(null);
    onDecided(msg);
  };

  async function act(s: Suggestion, op: 'approve' | 'decline') {
    setBusyId(s.id);
    const meta = KIND_META[s.kind];
    try {
      if (op === 'approve') await api.approveSuggestion(s.id);
      else await api.declineSuggestion(s.id);
      handled(op === 'approve' ? meta.approvedMsg : meta.declinedMsg);
    } catch (err) {
      toast.danger('Could not record the decision', err instanceof ApiError ? err.message : undefined);
      setBusyId(null);
    }
  }

  if (pending.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Inbox className="h-8 w-8 text-fg-subtle" aria-hidden />
            <p className="mt-3 text-sm font-medium text-fg">All clear</p>
            <p className="mt-1 text-sm text-fg-muted">Agent suggestions land here.</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <section className="rounded-xl border border-border bg-surface p-1.5">
        <div className="flex items-center gap-2 px-3 py-2.5">
          <span className="h-2 w-2 rounded-full bg-accent" aria-hidden />
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-fg-subtle">waiting for you</span>
        </div>
        <ul className="space-y-1.5 px-1.5 pb-1.5">
          {pending.map((s) => (
            <QueueRow
              key={s.id}
              s={s}
              canDecide={canDecide}
              busy={busyId === s.id}
              projectName={projectNameOf?.(s) ?? null}
              onOpen={() => setOpenId(s.id)}
              onAct={act}
            />
          ))}
        </ul>
      </section>

      {open && (
        <SuggestionPanel
          s={open}
          canDecide={canDecide}
          projectName={projectNameOf?.(open) ?? null}
          onClose={() => setOpenId(null)}
          onDecided={handled}
        />
      )}
    </>
  );
}

/* ------------------------------- one row ------------------------------- */
function Pill({ children, tone = 'ghost', disabled, onClick }: { children: ReactNode; tone?: 'primary' | 'ghost'; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={cn(
        'rounded-md px-3 py-1 text-2xs font-semibold uppercase tracking-[0.08em] transition disabled:opacity-50',
        tone === 'primary'
          ? 'border border-primary/50 bg-primary/10 text-primary hover:bg-primary/20'
          : 'border border-border text-fg-subtle hover:text-fg',
      )}
    >
      {children}
    </button>
  );
}

function QueueRow({
  s,
  canDecide,
  busy,
  projectName,
  onOpen,
  onAct,
}: {
  s: Suggestion;
  canDecide: boolean;
  busy: boolean;
  projectName: string | null;
  onOpen: () => void;
  onAct: (s: Suggestion, op: 'approve' | 'decline') => void;
}) {
  const meta = KIND_META[s.kind];
  return (
    <li className="rounded-lg border border-border bg-bg/50 px-3.5 py-3">
      <button onClick={onOpen} className="block w-full text-left">
        <div className="flex items-center gap-2">
          <meta.icon className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden />
          <span className="truncate font-medium text-fg">{s.title}</span>
        </div>
        {s.body.trim() && <p className="mt-1 line-clamp-2 pl-6 text-sm text-fg-muted">{stripMd(s.body)}</p>}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 pl-6 text-2xs uppercase tracking-[0.08em] text-fg-subtle">
          {projectName && <span>{projectName} ·</span>}
          <span>{meta.label}</span>
          <span>· {timeAgo(s.createdAt)}</span>
        </div>
      </button>

      {/* Interactive metadata (the tip) lives outside the open-row button. */}
      <div className="mt-2 flex items-center gap-1.5 pl-6">
        <ConfidenceBar value={s.confidence} className="max-w-56 flex-1" />
        <InfoTip label="What is confidence?">{CONFIDENCE_TIP}</InfoTip>
      </div>

      {canDecide && (
        <div className="mt-2.5 flex gap-2 pl-6">
          <Pill tone="primary" disabled={busy} onClick={() => onAct(s, 'approve')}>{busy ? '…' : meta.approve}</Pill>
          <Pill disabled={busy} onClick={onOpen}>Open</Pill>
          <Pill disabled={busy} onClick={() => onAct(s, 'decline')}>{meta.decline}</Pill>
        </div>
      )}
    </li>
  );
}

/* ------------------------------- the reader ------------------------------- */
function SuggestionPanel({
  s,
  canDecide,
  projectName,
  onClose,
  onDecided,
}: {
  s: Suggestion;
  canDecide: boolean;
  projectName: string | null;
  onClose: () => void;
  onDecided: (msg?: string) => void;
}) {
  const meta = KIND_META[s.kind];
  const [knowledge, setKnowledge] = useState(s.proposedKnowledge ?? '');
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const sources = sourceList(s.sources);

  async function decide(op: 'approve' | 'decline') {
    setBusy(true);
    try {
      if (op === 'approve') {
        // Persist an edited knowledge block first — what you approve is what gets stored.
        if (s.kind === 'LEARNING' && knowledge.trim() !== (s.proposedKnowledge ?? '').trim()) {
          await api.updateSuggestion(s.id, { proposedKnowledge: knowledge });
        }
        await api.approveSuggestion(s.id);
        onDecided(meta.approvedMsg);
      } else {
        await api.declineSuggestion(s.id, feedback.trim() || undefined);
        onDecided(meta.declinedMsg);
      }
    } catch (err) {
      toast.danger('Could not record the decision', err instanceof ApiError ? err.message : undefined);
      setBusy(false);
    }
  }

  return (
    <SidePanel
      title={s.title}
      subtitle={
        <span className="flex flex-wrap items-center gap-2">
          <Badge tone={s.kind === 'LEARNING' ? 'primary' : 'neutral'}>{meta.label}</Badge>
          {projectName && <span className="text-xs text-fg-subtle">{projectName}</span>}
          <span className="text-xs text-fg-subtle">{timeAgo(s.createdAt)}</span>
        </span>
      }
      onClose={onClose}
      footer={
        canDecide ? (
          <div className="space-y-2.5">
            <Field label="Feedback" htmlFor={`fb-${s.id}`} hint={`Optional. Sent to the agent when you ${meta.decline.toLowerCase()}.`}>
              <Input id={`fb-${s.id}`} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Why this wasn’t right…" />
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => decide('decline')}>{meta.decline}</Button>
              <Button size="sm" disabled={busy} onClick={() => decide('approve')}>{busy ? '…' : meta.approve}</Button>
            </div>
          </div>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <p className="rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs text-fg-muted">{meta.outcome}</p>
        <div className="flex items-center gap-1.5">
          <ConfidenceBar value={s.confidence} className="max-w-64 flex-1" />
          <InfoTip label="What is confidence?">{CONFIDENCE_TIP}</InfoTip>
        </div>

        <Markdown className="text-sm">{s.body}</Markdown>

        {s.kind === 'LEARNING' && (
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-fg-subtle">Knowledge to store on approval</p>
            {canDecide ? (
              <textarea
                value={knowledge}
                onChange={(e) => setKnowledge(e.target.value)}
                rows={8}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-xs leading-relaxed text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              />
            ) : (
              <Markdown className="rounded-lg border border-border bg-surface-2 p-3 text-sm">{knowledge || '_(empty)_'}</Markdown>
            )}
          </div>
        )}

        {sources.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 text-sm">
            <span className="text-fg-subtle">Sources:</span>
            {sources.map((src, i) => (
              <Badge key={i} tone="neutral">{src}</Badge>
            ))}
          </div>
        )}
      </div>
    </SidePanel>
  );
}

/* ------------------------------- decided history ------------------------------- */
export function DecidedList({ items, projectNameOf }: { items: Suggestion[]; projectNameOf?: (s: Suggestion) => string | null }) {
  if (items.length === 0) return null;
  return (
    <section>
      <p className="mb-2 mt-8 text-xs font-semibold uppercase tracking-[0.16em] text-fg-subtle">Decided</p>
      <ul className="divide-y divide-border">
        {items.map((s) => {
          const meta = KIND_META[s.kind];
          const name = projectNameOf?.(s) ?? null;
          return (
            <li key={s.id} className="flex items-start gap-3 py-2.5">
              <meta.icon className="mt-0.5 h-4 w-4 shrink-0 text-fg-subtle" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-fg">{s.title}</p>
                <p className="mt-0.5 text-xs text-fg-subtle">
                  {name ? `${name} · ` : ''}{meta.label}{s.reviewedAt ? ` · ${timeAgo(s.reviewedAt)}` : ''}
                  {s.feedback ? `: “${s.feedback}”` : ''}
                </p>
              </div>
              <Badge tone={s.status === 'APPROVED' ? 'success' : 'neutral'}>{s.status.toLowerCase()}</Badge>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
